import type { PepGenerateRequest } from '@/services/pep';
import type { PepProfile } from '@/services/pepProfile';

type AnyRecord = Record<string, unknown>;

function text(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function num(...values: unknown[]): number {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function normalizeDomain(value: string): string {
  let working = (value || '').trim().toLowerCase();
  if (!working) return '';
  if (working.includes('@')) working = working.split('@').pop() || '';
  working = working.replace(/^https?:\/\//, '').replace(/^www\./, '');
  working = working.split('/')[0] || '';
  working = working.split('?')[0] || '';
  working = working.split('#')[0] || '';
  return working.trim();
}

function formatLocation(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(formatLocation).filter(Boolean).join(' | ');
  if (typeof value === 'object') {
    const row = value as AnyRecord;
    const full = text(row.fullString);
    if (full) return full;
    const city = text(row.city);
    const state = text(row.stateAbbr, row.state);
    const country = text(row.countryAbbr, row.country);
    return [city, state, country].filter(Boolean).join(', ');
  }
  return '';
}

function mergeLeadAndResearch(lead: AnyRecord, researched: AnyRecord | null | undefined): AnyRecord {
  return { ...(lead || {}), ...(researched || {}) };
}

export function buildPepSequenceRequestFromResearchMatch(params: {
  tenantId: string;
  lead: AnyRecord;
  researched: AnyRecord | null | undefined;
  pepProfile?: PepProfile | null;
  pepCompanyProfile?: PepProfile | null;
  sourcePostUrl?: string;
  matchedProfileUrl?: string;
  commentText?: string;
  senderName?: string;
  senderCompany?: string;
  instantlyCampaignId?: string;
  instantlyCampaignName?: string;
  programType?: string;
  entrySource?: string;
  sourcePlatform?: string;
  matchedSocialProfileUrl?: string;
}): PepGenerateRequest {
  const merged = mergeLeadAndResearch(params.lead, params.researched);
  const sourcePlatform = text(params.sourcePlatform).toLowerCase() || 'linkedin';
  const programType = text(params.programType).toUpperCase() || (sourcePlatform === 'facebook' ? 'FACEBOOK_COMMENTER' : 'LINKEDIN_FULL');
  const leadId = text(merged.searchResultId, merged.sourceSearchResultId, merged.id, params.matchedProfileUrl, cryptoFallback(text(merged.Email, merged.email1, merged.email)));
  const companyName = text(merged.Company, merged.companyName, merged.company);
  const profileUrl = sourcePlatform === 'linkedin'
    ? text(merged.LIProfileUrl, merged.liUrl, merged.linkedInUrl, params.matchedProfileUrl)
    : '';
  const socialProfileUrl =
    sourcePlatform === 'facebook'
      ? text(
          merged.facebook_url,
          merged.facebookUrl,
          merged.FacebookProfileUrl,
          params.matchedSocialProfileUrl,
          params.matchedProfileUrl,
        )
      : text(params.matchedSocialProfileUrl, params.matchedProfileUrl);
  const email = text(merged.Email, merged.email1, merged.email);
  const contactName = text(merged.fullName, merged.Name, merged.name, `${text(merged.firstName)} ${text(merged.lastName)}`.trim());
  const firstName = text(merged.firstName, merged.first_name, contactName.split(/\s+/)[0]);
  const lastName = text(merged.lastName, merged.last_name, contactName.split(/\s+/).slice(1).join(' '));
  const title = text(merged.Title, merged.title);
  const phone = text(merged.contactPhone1, merged.contactMobilePhone1, merged.companyPhone1, merged.Phone);
  const domain = normalizeDomain(text(merged.CompanyDomain, merged.Website, merged.website, email));
  const fundingSummary = text(merged.CompanyFundingTotal);
  const headcount = num(merged.CompanyStaffCount);
  const industry = text(merged.CompanyIndustry, merged.industry);
  const sourcePostUrl = text(params.sourcePostUrl);
  const commentText = text(params.commentText);

  const signals: Record<string, unknown> = {
    job_listings: {
      score: 0,
      detected_at: null,
      summary: headcount > 0 ? `${headcount} employees` : null,
      roles: [],
      tools_mentioned: [],
      pain_keywords: [],
    },
    growth_announcements: {
      score: fundingSummary ? 35 : 0,
      detected_at: fundingSummary ? new Date().toISOString() : null,
      summary: fundingSummary ? `Funding/scale signal: ${fundingSummary}` : null,
      announcement_type: null,
      amount: fundingSummary || null,
      source: fundingSummary ? 'research' : null,
    },
    client_reviews: {
      score: 0,
      detected_at: null,
      summary: null,
      positive_themes: [],
      negative_themes: [],
      source: null,
    },
    social_activity: {
      score: sourcePostUrl || profileUrl ? 85 : 40,
      detected_at: new Date().toISOString(),
      summary: sourcePostUrl
        ? `Matched from ${sourcePlatform} engagement on ${sourcePostUrl}`
        : (profileUrl || socialProfileUrl)
          ? `Matched from ${sourcePlatform} profile during P.E.P.ai generation.`
          : 'Matched during P.E.P.ai generation.',
      post_topic: commentText || null,
      engagement_count: 1,
      platform: sourcePlatform,
      hashtag_listener_catch: true,
      caught_comment: commentText || null,
    },
    employee_sentiment: {
      score: 0,
      detected_at: null,
      summary: null,
      pain_themes: [],
      tools_mentioned: [],
      source: null,
    },
  };

  const fitCandidates = sourcePostUrl
    ? [{
        id: `${leadId}-source-post`,
        url: sourcePostUrl,
        text: commentText || `LinkedIn post relevant to ${contactName || companyName || 'target contact'}`,
        hashtags: [],
        mediaType: 'post',
        postedAt: new Date().toISOString(),
        commentsCount: 0,
        likesCount: 0,
      }]
    : [];

  return {
    tenantId: params.tenantId,
    leadId,
    contact: {
      id: leadId,
      first_name: firstName,
      last_name: lastName,
      title,
      company_name: companyName,
      industry,
      location: formatLocation((merged as AnyRecord).ContactLocation ?? (merged as AnyRecord).CompanyLocation),
      profile_url: profileUrl || '',
      facebook_url: sourcePlatform === 'facebook' ? (socialProfileUrl || null) : text(merged.facebook_url, merged.facebookUrl) || null,
      instagram_url: sourcePlatform === 'instagram' ? (socialProfileUrl || null) : text(merged.instagram_url, merged.instagramUrl) || null,
      email: email || null,
      phone: phone || null,
      stage: 1,
      gate_cleared: true,
      direct_reply_received: false,
      voicemail_permitted: false,
      do_not_contact: false,
      perpetual_heat_score: 0,
      potential_heat_score: 0,
      heat_velocity: 'FLAT',
      cold_days: 0,
      primary_channel:
        programType === 'FACEBOOK_COMMENTER' ? 'FACEBOOK' :
        programType === 'INSTAGRAM_SOCIAL' ? 'INSTAGRAM' :
        profileUrl ? 'LINKEDIN' : email ? 'EMAIL' : 'LINKEDIN',
      email_sends_last_14_days: 0,
      tags: ['PEPAI-research'],
      prior_touches: [],
      company_domain: domain || null,
    },
    signals,
    pepProfile: params.pepProfile || undefined,
    request: {
      action: 'START_SEQUENCE',
      channel: 'COMMENT',
      campaign_mode: 'BRAND',
      sender_name: params.senderName || 'Simply Scrapable',
      sender_company: params.senderCompany || 'Simply Scrapable',
      programType,
      entrySource: text(params.entrySource) || 'CONTACT_DRAWER',
      sourcePlatform: sourcePlatform.toUpperCase(),
      landing_page_url: null,
      industry_report_name: null,
      touch_number: 1,
      pep_profile: params.pepProfile || undefined,
      pep_profile_company: params.pepCompanyProfile || undefined,
      instantlyCampaignId: text(params.instantlyCampaignId) || undefined,
      instantlyCampaignName: text(params.instantlyCampaignName) || undefined,
      fit_check: fitCandidates.length
        ? {
            desired_action: 'comment',
            platform: sourcePlatform,
            min_score: 80,
            candidates: fitCandidates,
          }
        : undefined,
    },
  };
}

function cryptoFallback(value: string): string {
  return value ? `lead:${value.toLowerCase()}` : '';
}
